import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { Tooltip } from '@/components/Tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useJiraPriorities } from '@/hooks/useJiraPriorities';
import type { JiraPriorityConfig } from '@/types';

const GUARD_SEVERITIES = [
  { key: 'C', label: 'Critical' },
  { key: 'H', label: 'High' },
  { key: 'M', label: 'Medium' },
  { key: 'L', label: 'Low' },
  { key: 'I', label: 'Info' },
] as const;

const DEFAULT_MAPPINGS: Record<string, string> = {
  C: 'Highest',
  H: 'High',
  M: 'Medium',
  L: 'Low',
  I: 'Lowest',
};

interface JiraPriorityMappingProps {
  accountKey?: string;
  config?: Record<string, unknown>;
  value: JiraPriorityConfig | undefined;
  onChange: (value: JiraPriorityConfig | undefined) => void;
  disabled?: boolean;
}

export function JiraPriorityMapping({
  accountKey,
  config,
  value,
  onChange,
  disabled,
}: JiraPriorityMappingProps) {
  const enabled = value?.enabled ?? false;
  const useDefaults = value?.useDefaults ?? true;
  const mappings = value?.mappings ?? {};

  const { data, isLoading, isError } = useJiraPriorities({
    accountKey,
    config,
    enabled: enabled && !useDefaults,
  });
  const priorities = data?.priorities ?? [];
  const priorityNames = new Set(priorities.map(p => p.name));

  const handleEnabledChange = (checked: boolean) => {
    if (!checked) {
      onChange(undefined);
      return;
    }
    onChange({ enabled: true, useDefaults: true });
  };

  const handleUseDefaultsChange = (checked: boolean) => {
    if (!checked && priorities.length > 0) {
      const validatedMappings: Record<string, string> = {};
      for (const { key } of GUARD_SEVERITIES) {
        const def = DEFAULT_MAPPINGS[key];
        validatedMappings[key] = priorityNames.has(def)
          ? def
          : (priorities[0]?.name ?? '');
      }
      onChange({
        enabled: true,
        useDefaults: false,
        mappings: validatedMappings,
      });
    } else {
      onChange({
        enabled: true,
        useDefaults: checked,
        ...(!checked ? { mappings: { ...DEFAULT_MAPPINGS } } : {}),
      });
    }
  };

  const handleMappingChange = (severityKey: string, priorityName: string) => {
    const next = { ...mappings, [severityKey]: priorityName };
    onChange({ enabled: true, useDefaults: false, mappings: next });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id="priority-enabled"
          checked={enabled}
          onCheckedChange={handleEnabledChange}
          disabled={disabled}
        />
        <Label
          htmlFor="priority-enabled"
          className="text-chariot-text-primary cursor-pointer text-sm font-medium"
        >
          Enable priority matching
        </Label>
        <Tooltip
          placement="right"
          title="When enabled, tickets created from Guard will have their Jira priority set to match the risk severity. Changes to severity in Guard will update the ticket priority, and changes to priority in Jira will update the risk severity in Guard."
          tooltipClassName="max-w-xs"
        >
          <ExclamationCircleIcon className="size-4" aria-hidden="true" />
        </Tooltip>
      </div>

      {enabled && (
        <div className="ml-6 space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="priority-use-defaults"
              checked={useDefaults}
              onCheckedChange={handleUseDefaultsChange}
              disabled={disabled}
            />
            <Label
              htmlFor="priority-use-defaults"
              className="text-chariot-text-primary cursor-pointer text-sm"
            >
              Use default priorities
            </Label>
            <Tooltip
              placement="right"
              title="Default mapping: Critical → Highest, High → High, Medium → Medium, Low → Low, Info → Lowest. Uncheck to customize the mapping with your Jira instance's priority levels."
              tooltipClassName="max-w-xs"
            >
              <ExclamationCircleIcon className="size-4" aria-hidden="true" />
            </Tooltip>
          </div>

          {!useDefaults && (
            <div className="space-y-2">
              {isLoading ? (
                <p className="text-chariot-text-secondary text-xs">
                  Loading Jira priorities…
                </p>
              ) : isError ? (
                <p className="text-chariot-stroke-critical text-xs">
                  Failed to fetch Jira priorities. Check your credentials.
                </p>
              ) : priorities.length === 0 ? (
                <p className="text-chariot-text-secondary text-xs">
                  No priorities found in your Jira instance.
                </p>
              ) : (
                <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
                  {GUARD_SEVERITIES.map(({ key, label }) => (
                    <MappingRow
                      key={key}
                      severityLabel={label}
                      priorityOptions={priorities.map(p => p.name)}
                      value={
                        mappings[key] && priorityNames.has(mappings[key])
                          ? mappings[key]
                          : priorityNames.has(DEFAULT_MAPPINGS[key])
                            ? DEFAULT_MAPPINGS[key]
                            : ''
                      }
                      onChange={name => handleMappingChange(key, name)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface MappingRowProps {
  severityLabel: string;
  priorityOptions: string[];
  value: string;
  onChange: (name: string) => void;
  disabled?: boolean;
}

function MappingRow({
  severityLabel,
  priorityOptions,
  value,
  onChange,
  disabled,
}: MappingRowProps) {
  return (
    <>
      <span className="text-chariot-text-primary text-sm">{severityLabel}</span>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-8 w-full text-xs">
          <SelectValue placeholder="Select priority" />
        </SelectTrigger>
        <SelectContent>
          {priorityOptions.map(name => (
            <SelectItem key={name} value={name}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
}
